import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { ShieldLockFill, PenFill, BoxArrowRight } from "react-bootstrap-icons";
import notify from "../../common/ui/notify";
import SignaturePad from "../../common/SignaturePad";
import { AGREEMENT_TITLE, AGREEMENT_META, AGREEMENT_SECTIONS } from "./agreementContent";
import { t } from "./auctionI18n";
import { langUrl } from "../../common/langRouting";
import "./AgreementGate.css";

// App-wide gate: a logged-in bidder who has not accepted (and signed) the
// current LIVE-auction rules is blocked from using the system until they do.
// It re-checks whenever the user enters the /auction area, so a bidder who
// skipped acceptance at registration is caught again on their next login.
const AgreementGate = () => {
    const location = useLocation();
    const [blocked, setBlocked] = useState(false);
    const [agreed, setAgreed] = useState(false);
    const [signed, setSigned] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const sigRef = useRef(null);
    const acceptedKnown = useRef(false); // stop polling once we know it's accepted

    const check = useCallback(async () => {
        if (acceptedKnown.current) return;
        try {
            const res = await axios.get("/api/auction/me");
            const accepted = res.data && res.data.agreement && res.data.agreement.accepted;
            if (accepted) { acceptedKnown.current = true; setBlocked(false); }
            else setBlocked(true);
        } catch (err) {
            // 401 = not a bidder session (guests, staff) → nothing to gate.
            setBlocked(false);
        }
    }, []);

    useEffect(() => {
        if (location.pathname.startsWith("/auction")) check();
        else setBlocked(false);
    }, [location.pathname, check]);

    // Lock background scroll while the gate is up.
    useEffect(() => {
        if (!blocked) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, [blocked]);

    if (!blocked) return null;

    const accept = async () => {
        if (submitting) return;
        if (!agreed) { notify.warning({ geo: "ჯერ მონიშნეთ თანხმობა", en: "Please check the consent box first", ru: "Сначала отметьте согласие", am: "Նախ նշեք համաձայնությունը" }); return; }
        const dataUrl = sigRef.current && sigRef.current.toDataURL();
        if (!dataUrl || (sigRef.current && sigRef.current.isEmpty())) {
            notify.warning({ geo: "გასაგრძელებლად დააფიქსირეთ ხელმოწერა", en: "Please sign to continue", ru: "Пожалуйста, поставьте подпись", am: "Խնդրում ենք ստորագրել" });
            return;
        }
        setSubmitting(true);
        try {
            await axios.post("/api/auction/agreement/accept", { signature: dataUrl });
            notify.success({ geo: "პირობები მიღებულია", en: "Terms accepted", ru: "Условия приняты", am: "Պայմաններն ընդունվեցին" });
            // Reload so every view re-reads the (now accepted) status.
            window.location.reload();
        } catch (err) {
            notify.apiError(err);
            setSubmitting(false);
        }
    };

    const leave = async () => {
        try { await axios.post("/api/auction/logout"); } catch (_) { /* ignore */ }
        window.location.href = langUrl("/auction/login");
    };

    return (
        <div className="agreement-gate" role="dialog" aria-modal="true" aria-labelledby="agreement-gate-title">
            <div className="agreement-gate-card">
                <div className="agreement-gate-head">
                    <span className="agreement-gate-badge"><ShieldLockFill /> {t("სავალდებულო თანხმობა", "Mandatory consent", "Обязательное согласие", "Պարտադիր համաձայնություն")}</span>
                    <h2 id="agreement-gate-title">{AGREEMENT_TITLE}</h2>
                    <p className="agreement-gate-lead">
                        {t(
                            "სისტემით სარგებლობის გასაგრძელებლად გაეცანით წესებსა და პირობებს, დაეთანხმეთ და დააფიქსირეთ ხელმოწერა. თანხმობის გარეშე აუქციონში მონაწილეობა შეუძლებელია.",
                            "To continue using the system, read the terms and conditions, accept them and sign. Participation in the auction is not possible without consent.",
                            "Чтобы продолжить пользоваться системой, ознакомьтесь с условиями, примите их и поставьте подпись. Без согласия участие в аукционе невозможно.",
                            "Համակարգից օգտվելը շարունակելու համար ծանոթացեք պայմաններին, ընդունեք դրանք և ստորագրեք։ Առանց համաձայնության աճուրդին մասնակցությունն անհնար է։"
                        )}
                    </p>
                </div>

                <div className="agreement-gate-doc">
                    <ul className="agreement-gate-meta">
                        {AGREEMENT_META.map((m, i) => <li key={i}>{m}</li>)}
                    </ul>
                    {AGREEMENT_SECTIONS.map((s, i) => (
                        <section key={i} className="agreement-gate-section">
                            <h3>{s.title}</h3>
                            {s.items.map((p, j) => <p key={j}>{p}</p>)}
                        </section>
                    ))}

                    <div className="agreement-gate-sign">
                    <label className="agreement-gate-consent">
                        <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
                        <span>{t("გავეცანი და ვეთანხმები LIVE ონლაინ აუქციონის წესებსა და პირობებს.", "I have read and accept the terms and conditions of the LIVE online auction.", "Я ознакомился и принимаю условия LIVE онлайн-аукциона.", "Ես ծանոթացել և ընդունում եմ LIVE առցանց աճուրդի պայմանները։")}</span>
                    </label>

                    <div className="agreement-gate-sigwrap">
                        <div className="agreement-gate-sig-label"><PenFill /> {t("ხელმოწერა (დახაზეთ მაუსით)", "Signature (draw with the mouse)", "Подпись (нарисуйте мышью)", "Ստորագրություն (նկարեք մկնիկով)")}</div>
                        <SignaturePad ref={sigRef} onChange={setSigned} />
                        <div className="agreement-gate-sig-actions">
                            <span className="agreement-gate-hint">{signed ? t("ხელმოწერა დაფიქსირდა", "Signature captured", "Подпись зафиксирована", "Ստորագրությունը գրանցված է") : t("დახაზეთ თქვენი ხელმოწერა ზემოთ", "Draw your signature above", "Нарисуйте подпись выше", "Նկարեք ձեր ստորագրությունը վերևում")}</span>
                            <button type="button" className="agreement-gate-clear" onClick={() => { sigRef.current && sigRef.current.clear(); setSigned(false); }}>{t("გასუფთავება", "Clear", "Очистить", "Մաքրել")}</button>
                        </div>
                    </div>
                    </div>
                </div>

                <div className="agreement-gate-actions">
                    <button type="button" className="agreement-gate-leave" onClick={leave} disabled={submitting}>
                        <BoxArrowRight /> {t("გასვლა", "Sign out", "Выйти", "Դուրս գալ")}
                    </button>
                    <button type="button" className="agreement-gate-accept" onClick={accept} disabled={submitting || !agreed || !signed}>
                        {submitting ? t("იგზავნება…", "Sending…", "Отправка…", "Ուղարկվում է…") : t("ვეთანხმები და ხელს ვაწერ", "I agree and sign", "Я согласен и подписываю", "Համաձայն եմ և ստորագրում եմ")}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AgreementGate;
